import type { Links, OpsOverview as OpsPayload } from '@ops/contracts'
import { formatPeriodo } from '@ops/contracts'

import { irA } from '../../lib/scroll'
import { formatMetricValue, formatRelativeDays, plural } from '../../lib/format'
import {
  Button,
  HeroKpi,
  KpiCard,
  Section,
  Stack,
  TriageBar,
  TwoCol,
} from '../../ui'
import { AgingChart } from './AgingChart'
import { ClientesPanel } from './ClientesPanel'
import { DeudaDatosCard } from './DeudaDatosCard'
import {
  baseDe,
  baseDelFrente,
  metricaDelFrente,
  porSlug,
} from './derivar'
import { FollowupsRail } from './FollowupsRail'
import { FrenteCard, FrentesLista } from './FrenteCard'
import { Vacio } from '../comunes'
import {
  claveEntidad,
  frentesDeAlertas,
  indiceCobertura,
  metricasEnMovimiento,
  metricasSanas,
  ultimaCarga,
} from './frentes'
import { deudaDeDatos } from './deuda'
import { brechaTotal, diasMaximos, titularOps } from './narrativa'
import type { Tablero } from './tablero'
import { Titular } from './Titular'
import { tableroDeTriage } from './triage'
import { ZonaSana } from './ZonaSana'

/**
 * Overview de ops/delivery — "¿qué tengo que mover hoy?".
 *
 * Misma estructura de zonas que el founder, pero los frentes son la cola de
 * implementación con su aging, la brecha de activación y las cuentas en riesgo.
 *
 * **Sin un solo dato de $$.** El recorte real lo hace el servidor (`Client.mrr`
 * llega en null), y esta pantalla además no dibuja ninguna columna ni ninguna
 * oración de plata: una columna vacía le contaría a ops que existe un número que
 * no puede ver.
 */
export interface OpsOverviewProps {
  payload: OpsPayload & { links: Links }
  tablero: Tablero
}

const ZONA_1 = 'zona-1'
const DEUDA = 'deuda-datos'

export function OpsOverviewVista({ payload, tablero }: OpsOverviewProps) {
  const { now, meta } = tablero

  const cola = porSlug(payload.heroMetrics, 'pendientes_implementacion')
  const brecha = porSlug(payload.heroMetrics, 'brecha_activacion_total')
  const resto = payload.heroMetrics.filter(
    (m) =>
      m.slug !== 'pendientes_implementacion' &&
      m.slug !== 'brecha_activacion_total',
  )

  const criticos = frentesDeAlertas(payload.alerts, 'critical')
  // Misma corrección que en el Overview del founder: la deuda sale de las
  // alertas del payload, que cubren todo el universo de métricas del rol, y no
  // solo de las cuatro de la fila héroe.
  const deuda = deudaDeDatos(payload.alerts, payload.heroMetrics, meta.valorDe)
  const faltantes = deuda.sinDato
  const viejas = deuda.vencidas
  const sanas = metricasSanas(resto)
  const moviendose = metricasEnMovimiento(resto)

  const cobertura = indiceCobertura(payload.misFollowups, payload.links)
  const espera = diasMaximos(payload.colaImplementacion)
  const empleadosSinInstalar = brechaTotal(payload.brechaCritica)
  const cargado = ultimaCarga(payload.heroMetrics)

  const triage = tableroDeTriage(
    [
      {
        severidad: 'critical',
        cantidad: payload.enRiesgo.length,
        label: 'cuentas en riesgo',
        labelSingular: 'cuenta en riesgo',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'warning',
        cantidad: payload.colaImplementacion.length,
        label: 'cuentas esperando implementación',
        labelSingular: 'cuenta esperando implementación',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'warning',
        cantidad: payload.brechaCritica.length,
        label: 'cuentas con brecha de activación',
        labelSingular: 'cuenta con brecha de activación',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'unknown',
        cantidad: faltantes.length,
        label: 'números sin dato',
        labelSingular: 'número sin dato',
        onClick: () => irA(DEUDA),
      },
      {
        severidad: 'ok',
        cantidad: sanas.length,
        label: 'frentes en target',
        labelSingular: 'frente en target',
      },
    ],
    'La cola está vacía y ninguna cuenta pide atención',
  )

  return (
    <Stack gap="lg">
      <Titular
        titular={titularOps(payload, deuda, now)}
        periodo={formatPeriodo(payload.periodo)}
      />

      {cola === null ? (
        <Vacio icono={null}
          mensaje="El tablero de ops no tiene todavía su métrica de cola"
          detalle="Las métricas de la fila héroe se definen en el catálogo, en Admin."
          punteada
        />
      ) : (
        <HeroKpi
          metric={cola}
          secundaria={brecha}
          base={baseDe(cola)}
          direccion={meta.direccionDe(cola.slug)}
          aside={<AgingChart clientes={payload.colaImplementacion} />}
          onLoadData={() => tablero.cargarDato(cola)}
          now={now}
          acciones={
            <Button variante="secundario" onClick={() => tablero.verDetalle(cola)}>
              Ver definición y umbrales
            </Button>
          }
        />
      )}

      <TriageBar
        estado={triage.estado}
        titular={triage.titular}
        conteos={triage.conteos}
        meta={
          cargado === null
            ? 'ninguna métrica tiene carga registrada'
            : `carga más reciente: ${formatRelativeDays(cargado, now)}`
        }
        acciones={
          <>
            <Button variante="primario" onClick={() => irA(ZONA_1)}>
              Atacar el cuello de botella (
              {formatMetricValue(payload.colaImplementacion.length, 'count')}{' '}
              {plural(payload.colaImplementacion.length, 'cuenta', 'cuentas')})
            </Button>
            <Button variante="secundario" onClick={() => irA(DEUDA)}>
              Cargar los datos que faltan (
              {formatMetricValue(faltantes.length + viejas.length, 'count')})
            </Button>
          </>
        }
        className="rounded-card border border-border"
      />

      <Section
        id={ZONA_1}
        kicker="Zona 1"
        titulo="Lo que está roto"
        bajada={
          <>
            <b>{payload.colaImplementacion.length}</b>{' '}
            {plural(
              payload.colaImplementacion.length,
              'cuenta firmada espera implementación',
              'cuentas firmadas esperan implementación',
            )}
            , la más vieja hace <b>{espera}</b> días.{' '}
            <b>{empleadosSinInstalar}</b>{' '}
            {plural(
              empleadosSinInstalar,
              'empleado digital contratado no está instalado',
              'empleados digitales contratados no están instalados',
            )}{' '}
            en las cuentas de mayor brecha, y <b>{payload.enRiesgo.length}</b>{' '}
            {plural(payload.enRiesgo.length, 'cuenta está', 'cuentas están')} en
            retención.
          </>
        }
      >
        <TwoCol
          ratio="rail"
          asideSticky
          aside={
            <FollowupsRail
              grupos={payload.misFollowups}
              links={payload.links}
              titulo="Mis seguimientos"
              vacio="No tenés seguimientos abiertos. Asignate uno desde cualquier fila y se crea el work item en Plane."
            />
          }
        >
          <Stack gap="md">
            <ClientesPanel
              variante="riesgo"
              clientes={payload.enRiesgo}
              links={payload.links}
              cobertura={cobertura}
              onCrearSeguimiento={tablero.seguirCliente}
              titulo="Las cuentas que se pueden ir"
              subtitulo="En retención, ordenadas por antigüedad en el estado"
              vacio="Ninguna cuenta está en retención. Nada que recuperar hoy."
              footer="Una cuenta sin último contacto registrado es una cuenta que nadie llamó."
              sistema="plataforma"
            />

            <ClientesPanel
              variante="cola"
              clientes={payload.colaImplementacion}
              links={payload.links}
              cobertura={cobertura}
              onCrearSeguimiento={tablero.seguirCliente}
              titulo="La cola de implementación, de la más vieja a la más nueva"
              subtitulo="Cuentas firmadas sin su primer empleado digital en producción"
              vacio="No hay cuentas esperando implementación."
              sistema="plataforma"
            />

            <ClientesPanel
              variante="brecha"
              clientes={payload.brechaCritica}
              links={payload.links}
              cobertura={cobertura}
              onCrearSeguimiento={tablero.seguirCliente}
              titulo="Empleados digitales contratados que no están corriendo"
              subtitulo="Las cuentas de mayor brecha entre contratado e instalado"
              vacio="Ninguna cuenta tiene brecha de activación abierta."
              footer="La brecha sale de la plataforma. Una cuenta sin medición no aparece acá, así que la brecha real puede ser mayor."
              sistema="plataforma"
            />

            <FrentesLista
              frentes={criticos}
              vacio={
                <Vacio icono={null}
                  mensaje="Ninguna alerta crítica más allá de las cuentas de arriba"
                  detalle="No hay métricas de delivery fuera de umbral."
                />
              }
            >
              {(frente) => (
                <FrenteCard
                  key={frente.id}
                  frente={frente}
                  owner={
                    frente.entityType === 'metric'
                      ? meta.ownerDe(frente.entityId)
                      : null
                  }
                  cobertura={cobertura.get(
                    claveEntidad(frente.entityType, frente.entityId),
                  )}
                  onCrearSeguimiento={tablero.seguirFrente}
                  metric={metricaDelFrente(payload.heroMetrics, frente)}
                  base={baseDelFrente(payload.heroMetrics, frente)}
                  direccion={
                    frente.entityType === 'metric'
                      ? meta.direccionDe(frente.entityId)
                      : undefined
                  }
                  now={now}
                  onVerDetalle={tablero.verDetalle}
                />
              )}
            </FrentesLista>

            <DeudaDatosCard
              id={DEUDA}
              sinDato={faltantes}
              vencidas={viejas}
              onCargar={tablero.cargarDato}
              ownerDe={meta.ownerDe}
              now={now}
            />
          </Stack>
        </TwoCol>
      </Section>

      <Section
        kicker="Zona 2"
        titulo="Lo que se está moviendo"
        bajada={
          moviendose.length === 0
            ? 'Ninguna métrica de delivery está en ámbar: lo que hay es lo roto de arriba y lo sano de abajo.'
            : 'Métricas en ámbar: todavía no están fuera de umbral, pero vienen para ese lado.'
        }
      >
        {moviendose.length === 0 ? (
          <Vacio icono={null} mensaje="Nada en ámbar en el período." />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {moviendose.map((metric) => (
              <KpiCard
                key={metric.slug}
                metric={metric}
                base={baseDe(metric)}
                direccion={meta.direccionDe(metric.slug)}
                onLoadData={() => tablero.cargarDato(metric)}
                onOpenDetail={() => tablero.verDetalle(metric)}
                now={now}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        kicker="Zona 3"
        titulo="Lo que está sano"
        bajada="Colapsado: si el frente está en target, no pide atención hoy."
        regla={false}
        espaciado="sm"
      >
        <ZonaSana
          metricas={sanas}
          direccionDe={meta.direccionDe}
          onOpenDetail={tablero.verDetalle}
          now={now}
        />
      </Section>
    </Stack>
  )
}

